import { User } from 'oidc-client';
import { AuthApi } from '../apis';
import { getEnvironmentName } from '../config';
import { UserType } from '../types';

type UserCache = { [userType in UserType]: User };

export class AuthService {
  private userCache = {} as UserCache;

  public onSigningIn: () => void = () => {};
  public onSignedIn: (success: boolean) => Promise<void> = async () => {};
  public onSignedOut: () => void = () => {};
  public onSigningOut: (message: string) => void = () => {};

  public async getUser(type?: UserType): Promise<User> {
    let user = this.userCache[type ?? ''] as User;
    if (!user || user.expired || user.expired == null) {
      const userSettings =
        type === 'pwdi'
          ? await AuthApi.fetchPwdiUserSettings()
          : await AuthApi.fetchUserSettings();
      user = new User(userSettings);
      this.userCache[type ?? ''] = user;
    }

    return user;
  }

  public async signinSilent(): Promise<boolean> {
    try {
      await Promise.all([this.getUser(), this.getUser('pwdi')]);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Kicks off the login flow. Must trigger this method
   * from explicit user interaction (read: a button or link click)
   * as opposed to a redirect or some browsers will complain and attempt
   * to block the pop-up window.
   *
   * If the authentication is successful, we try to fetch tokens from the server.
   * After that we invoke onSignedIn() callback with a boolean value that
   * indicated if sign in was successful or not.
   */
  public signin() {
    this.onSigningIn();

    microsoftTeams.authentication.authenticate({
      url: '/home/login',
      height: 600,
      width: 600,

      successCallback: (result?: string) => {
        this.signinSilent()
          .then(async success => this.onSignedIn(success))
          .catch(error => console.error(error));
      },
      failureCallback: (reason?: string) => {
        console.warn('Teams authentication pop-up failure', reason);
        window.location.reload();
      },
    });
  }

  /**
   * Ends the user session. Must trigger this method
   * from explicit user interaction (read: a button or link click)
   * as opposed to a redirect or some browsers will complain and attempt
   * to block the pop-up window.
   */
  public async signout(message?: string): Promise<void> {
    this.onSigningOut(message ?? 'Signing out of the service...');
    let signedOutCallback: (value: void | PromiseLike<void>) => void;
    const promise = new Promise<void>(resolve => (signedOutCallback = resolve));

    this.userCache = {} as UserCache;

    // Redirects are not allowed in Teams and should only happen in a pop-up.
    // The only easy way to do this it to use authenticate() pop-up.
    microsoftTeams.authentication.authenticate({
      url: '/home/logout',
      height: 600,
      width: 600,

      successCallback: (result?: string) => {
        signedOutCallback();
        this.onSignedOut();
      },
      failureCallback: (reason?: string) => {
        console.warn('Teams authentication pop-up failure', reason);
        window.location.reload();
      },
    });

    return promise;
  }

  public userIsAdmin(user?: User): boolean {
    if (!user?.profile?.role) {
      return false;
    }

    const roles =
      typeof user.profile.role === 'string'
        ? [user.profile.role]
        : (user.profile.role as string[]);

    return roles.some(
      role =>
        role === 'Account Admin' ||
        role === 'SITE_ADMINISTRATOR' ||
        role === 'Project Manager'
    );
  }

  public getFullName(user?: User): string {
    const firstName = user?.profile.given_name;
    const lastName = user?.profile.family_name;
    return firstName ? (lastName ? `${firstName} ${lastName}` : firstName) : '';
  }

  public getInitials(user?: User): string {
    const fullName = this.getFullName(user);
    const names = fullName.split(' ').map(name => name.charAt(0));
    return names.length > 1 ? names[0] + names[names.length - 1] : names[0];
  }

  public getProfileLink() {
    const env = ['local', 'dev', 'qa'].includes(
      getEnvironmentName().toLowerCase()
    )
      ? `qa-`
      : '';
    return `https://${env}ims.bentley.com/IMS/Profile`;
  }
}

export default new AuthService();
